/*
 * Identity based memoization, ideal for working with data being fed to React components.
 * Code from: https://github.com/reduxjs/reselect/blob/master/src/index.js, modified to
 * fit our linter.
 */
const defaultEqualityCheck = (a: $TSFixMe, b: $TSFixMe) => {
  return a === b;
};

const areArgumentsShallowlyEqual = (equalityCheck: $TSFixMe, prev: $TSFixMe, next: $TSFixMe) => {
  if (prev === null || next === null || prev?.length !== next?.length) {
    return false;
  }

  const length = prev.length;
  for (let i = 0; i < length; i += 1) {
    if (!equalityCheck(prev[i], next[i])) {
      return false;
    }
  }

  return true;
};

export const defaultMemoize = (func: $TSFixMe, equalityCheck = defaultEqualityCheck) => {
  let lastArgs: $TSFixMe;
  let lastResult: $TSFixMe;
  return function (...args: $TSFixMe[]) {
    if (!areArgumentsShallowlyEqual(equalityCheck, lastArgs, args)) {
      lastResult = func(...args);
    }

    lastArgs = args;
    return lastResult;
  };
};

function getDependencies(funcs: $TSFixMe) {
  const dependencies = Array.isArray(funcs[0]) ? funcs[0] : funcs;

  if (!dependencies.every((dep: $TSFixMe) => typeof dep === 'function')) {
    const dependencyTypes = dependencies.map((dep: $TSFixMe) => typeof dep).join(', ');
    throw new Error(
      'Selector creators expect all input-selectors to be functions, ' +
        `instead received the following types: [${dependencyTypes}]`
    );
  }

  return dependencies;
}

export const createSelectorCreator = (memoize: $TSFixMe, ...memoizeOptions: $TSFixMe[]) => {
  return (...funcs: $TSFixMe[]) => {
    let recomputations = 0;
    const resultFunc = funcs.pop();
    const dependencies = getDependencies(funcs);

    const memoizedResultFunc = memoize(function (...args: $TSFixMe[]) {
      recomputations += 1;
      return resultFunc(...args);
    }, ...memoizeOptions);

    const selector = memoize(function (...args: $TSFixMe[]) {
      const params: ((...x: any[]) => any)[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any
      const length = dependencies.length;

      for (let i = 0; i < length; i += 1) {
        params.push(dependencies[i](...args));
      }

      return memoizedResultFunc(...params);
    });

    selector.resultFunc = resultFunc;
    selector.dependencies = dependencies;
    selector.recomputations = () => recomputations;
    selector.resetRecomputations = () => {
      recomputations = 0;
    };
    return selector;
  };
};

export const createSelector = createSelectorCreator(defaultMemoize);

export function createStructuredSelector(selectors: $TSFixMe, selectorCreator = createSelector) {
  if (typeof selectors !== 'object') {
    throw new Error(
      'createStructuredSelector expects first argument to be an object ' +
        `where each property is a selector, instead received a ${typeof selectors}`
    );
  }
  const objectKeys = Object.keys(selectors);
  return selectorCreator(
    objectKeys.map((key) => selectors[key]),
    (...values: $TSFixMe[]) => {
      return values.reduce((composition, value, index) => {
        // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        composition[objectKeys[index]] = value; // eslint-disable-line no-param-reassign
        return composition;
      }, {});
    }
  );
}
